// Custom-integration detail panel for the audit-tool-invocations page.
// Extracted so the rendering can be exercised in isolation by tests.

import { Badge } from "@/components/ui/badge";

export interface CustomResultData {
  integration?: string;
  endpoint?: string;
  status?: number;
  ok?: boolean;
  truncated?: boolean;
  // Set by the server-side audit-log cap (server/tools/custom.ts ::
  // capResultJsonForAudit) when the persisted JSON would have exceeded
  // RESULT_JSON_MAX_BYTES. Distinct from `truncated`, which can also be
  // set by the upstream network layer at MAX_RESPONSE_BYTES.
  auditTruncated?: boolean;
  originalBytes?: number;
  body?: unknown;
  error?: string;
}

export function formatBody(body: unknown): string {
  if (body === null || body === undefined) return "(empty response body)";
  if (typeof body === "string") return body;
  try {
    return JSON.stringify(body, null, 2);
  } catch {
    return String(body);
  }
}

export function HttpStatusBadge({ status }: { status: number | undefined }): JSX.Element | null {
  if (typeof status !== "number") return null;
  const variant: "default" | "destructive" | "secondary" =
    status >= 200 && status < 300 ? "default" : status >= 500 ? "destructive" : "secondary";
  const cls = status >= 200 && status < 300 ? "bg-blue-600 hover:bg-blue-600" : "";
  return (
    <Badge variant={variant} className={cls} data-testid="badge-http-status">
      HTTP {status}
    </Badge>
  );
}

export function OkBadge({ ok }: { ok: boolean | undefined }): JSX.Element | null {
  if (ok === undefined) return null;
  return ok ? (
    <Badge variant="default" className="bg-emerald-600 hover:bg-emerald-600" data-testid="badge-custom-ok">
      ok
    </Badge>
  ) : (
    <Badge variant="destructive" data-testid="badge-custom-failed">
      failed
    </Badge>
  );
}

export interface CustomDetailPanelProps {
  rowId: string;
  custom: CustomResultData;
}

export function CustomDetailPanel({ rowId, custom }: CustomDetailPanelProps): JSX.Element {
  return (
    <div
      className="mb-3 rounded border bg-background p-3 space-y-2"
      data-testid={`panel-custom-${rowId}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">Custom integration call</span>
        <OkBadge ok={custom.ok} />
        <HttpStatusBadge status={custom.status} />
        {custom.truncated && (
          <Badge variant="outline" data-testid="badge-custom-truncated">
            {custom.auditTruncated ? "trimmed for audit log" : "response truncated"}
          </Badge>
        )}
        <span className="text-xs text-muted-foreground">
          {custom.integration ? `${custom.integration}` : ""}
          {custom.endpoint ? ` · ${custom.endpoint}` : ""}
        </span>
      </div>
      {custom.error && (
        <div className="text-xs text-destructive" data-testid={`text-custom-error-${rowId}`}>
          {custom.error}
        </div>
      )}
      {custom.body !== undefined && (
        <div>
          <div className="text-xs font-medium text-muted-foreground mb-1">Upstream response body</div>
          <pre
            data-testid={`text-custom-body-${rowId}`}
            className="text-xs bg-muted/40 border rounded p-2 overflow-auto max-h-72 whitespace-pre-wrap break-all"
          >
            {formatBody(custom.body)}
          </pre>
        </div>
      )}
    </div>
  );
}
